// useIcmEligibility.js
//
// Whether the current location (network) is ICM eligible, from GET_ICM_ELIGIBILITY. Used by the menu to hide
// device-group Instant WiFi on ICM-eligible locations. One request per location: the answer is cached, and
// callers that ask while a request is in flight share it.
//
// Returns true or false once known, and null while unknown (not enabled, no current network, or loading).
import { useEffect, useState } from 'react';
import useApi from 'hooks/useApi';

// useApi is a plain async request function despite its name (the Instant WiFi page aliases it the same way).
const apiCall = useApi;

const eligibilityByNetwork = new Map();
const pendingByNetwork = new Map();

const resolveNetworkId = () => {
  try {
    return localStorage.getItem('currentNetwork') || '';
  } catch {
    return '';
  }
};

// Fetches (once per network) and caches the eligibility. A failed or unsuccessful request counts as not
// eligible, matching the Instant WiFi page, but is not cached so a later render can try again.
export const fetchIcmEligibility = (networkId) => {
  if (eligibilityByNetwork.has(networkId)) return Promise.resolve(eligibilityByNetwork.get(networkId));
  if (pendingByNetwork.has(networkId)) return pendingByNetwork.get(networkId);

  const request = apiCall('GET_ICM_ELIGIBILITY', { networkId }, {}, [], [])
    .then((result) => {
      if (!result?.response?.status) return false;
      const eligible = result.icmEligible === true;
      eligibilityByNetwork.set(networkId, eligible);
      return eligible;
    })
    .catch(() => false)
    .finally(() => pendingByNetwork.delete(networkId));

  pendingByNetwork.set(networkId, request);
  return request;
};

// Test helper: forget cached answers.
export const clearIcmEligibilityCache = () => {
  eligibilityByNetwork.clear();
  pendingByNetwork.clear();
};

// `networkIdOverride` defaults to the current network in localStorage; pass the OrganizationContext value to
// follow location switches as they happen.
const useIcmEligibility = (enabled = true, networkIdOverride) => {
  const networkId = enabled ? (networkIdOverride ?? resolveNetworkId()) || '' : '';
  const [eligibility, setEligibility] = useState(() =>
    networkId && eligibilityByNetwork.has(networkId) ? eligibilityByNetwork.get(networkId) : null
  );

  useEffect(() => {
    if (!networkId) {
      setEligibility(null);
      return undefined;
    }
    let active = true;
    setEligibility(eligibilityByNetwork.has(networkId) ? eligibilityByNetwork.get(networkId) : null);
    fetchIcmEligibility(networkId).then((eligible) => {
      if (active) setEligibility(eligible);
    });
    return () => {
      active = false;
    };
  }, [networkId]);

  return eligibility;
};

export default useIcmEligibility;
